/**
 * Переключатель под полем ответа «Чатов»: что уйдёт по «Отправить».
 *
 * «Сообщение» — клиенту, как всегда (по умолчанию). «Комментарий» — заметка к сделке чата в CRM:
 * клиент её не видит, в CRM она видна в сделке, договоре и карточке клиента. «В салон» — тот же
 * комментарий и вопрос по заказу в группу Telegram «Общее <город>»; ответы реплаем в группе лягут
 * в CRM ответами на комментарий и появятся в ленте чата. Без сделки у чата комментарий привязать
 * некуда — оба режима закрыты. Спека `docs/specs/2026-10-23-chat-compose-modes-design.md`.
 *
 * «Замечание» (право `qc_remark`) — режим выделения контроля качества: кружки у сообщений и звонков
 * ленты, вместо поля ответа — поле замечания; переключатель остаётся под ним, другой режим —
 * выход из замечаний. Спека `docs/specs/2026-10-25-qc-remarks-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { ClipboardCheck, MessageCircle, Store, StickyNote } from "lucide-react";

import { useQc } from "@/components/qc/QcSelection";
import { type ComposeMode, loadSalonForm } from "@/lib/compose";

const MODES: { id: ComposeMode; label: string; icon: typeof MessageCircle; hint: string }[] = [
  { id: "message", label: "Сообщение", icon: MessageCircle, hint: "Сообщение клиенту в этот чат" },
  {
    id: "comment",
    label: "Комментарий",
    icon: StickyNote,
    hint: "Комментарий к сделке в CRM — клиент не увидит; виден в сделке, договоре и карточке клиента",
  },
  {
    id: "salon",
    label: "В салон",
    icon: Store,
    hint: "Комментарий к сделке + вопрос в группу салона города; ответ реплаем в группе ляжет ответом на комментарий",
  },
];

export const salonQuery = (chatId: number, enabled: boolean) => ({
  queryKey: ["chat-salon", chatId],
  queryFn: () => loadSalonForm(chatId),
  enabled,
  staleTime: 60_000,
});

export function ComposeModeBar({
  chatId,
  mode,
  onMode,
  deal,
  city,
  onCity,
}: {
  chatId: number;
  mode: ComposeMode;
  onMode: (m: ComposeMode) => void;
  /** Сделка чата: без неё комментарий и вопрос в салон не привязать. */
  deal: number | null;
  /** Выбранный руками город вопроса; `null` — город чата. */
  city: number | null;
  onCity: (id: number | null) => void;
}) {
  const qc = useQc();
  const noting = !!qc?.active && qc.purpose === "remark";
  const salon = useQuery(salonQuery(chatId, mode === "salon" && !noting));
  const form = salon.data;
  const chosen = city ?? form?.city ?? null;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 px-1 text-[13px]" role="radiogroup" aria-label="Что отправить" data-annot="тип сообщения">
      {MODES.map(({ id, label, icon: Icon, hint }) => {
        const closed = (id !== "message" && !deal) || (noting && !!qc?.busy);
        const on = mode === id && !noting;
        return (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={closed}
            onClick={() => {
              if (noting) qc?.stop();
              onMode(id);
            }}
            title={
              noting && qc?.busy
                ? "Замечание ещё сохраняется — подождите несколько секунд"
                : closed
                  ? "У чата нет сделки — комментарий привязать некуда"
                  : hint
            }
            className={`flex items-center gap-1 rounded-full px-2.5 py-1 font-medium transition-colors disabled:opacity-40 ${
              on
                ? id === "message"
                  ? "bg-[var(--tg-primary)] text-white"
                  : "bg-warn text-white"
                : "bg-[var(--tg-surface)] text-[var(--tg-sec)] shadow-[0_1px_3px_rgba(0,0,0,.12)] hover:text-[var(--tg-text)]"
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        );
      })}
      {qc?.add && (
        <button
          type="button"
          role="radio"
          aria-checked={noting}
          disabled={noting && qc.busy}
          onClick={() => (noting ? qc.stop() : qc.start())}
          title={
            noting
              ? "Выйти из замечаний (Esc) — черновик останется"
              : "Замечание контроля качества текстом или голосом: ко всему чату или к сообщениям и звонкам, отмеченным кружками слева"
          }
          className={`flex items-center gap-1 rounded-full px-2.5 py-1 font-medium transition-colors disabled:opacity-50 ${
            noting
              ? "bg-[var(--tg-primary)] text-white"
              : "bg-[var(--tg-surface)] text-[var(--tg-sec)] shadow-[0_1px_3px_rgba(0,0,0,.12)] hover:text-[var(--tg-text)]"
          }`}
          data-annot="режим замечания"
        >
          <ClipboardCheck size={14} /> Замечание
          {noting && qc.picked.size > 0 && <span className="tabular-nums">· {qc.picked.size}</span>}
        </button>
      )}
      {mode === "salon" && !noting && (
        <label className="flex min-w-0 items-center gap-1 text-[var(--tg-sec)]" data-annot="город вопроса">
          <span>в</span>
          {salon.isPending ? (
            <span>…</span>
          ) : salon.isError ? (
            <span className="text-[#e53935]">{salon.error.message}</span>
          ) : (
            <select
              value={chosen ?? ""}
              onChange={(e) => onCity(e.target.value ? Number(e.target.value) : null)}
              className="min-w-0 max-w-[220px] rounded-full bg-[var(--tg-surface)] px-2 py-1 text-[var(--tg-text)] shadow-[0_1px_3px_rgba(0,0,0,.12)] outline-none"
              title={form?.stylist ? `Отметим ${form.stylist.name} — вела примерку` : "Отметить некого — примерок по сделке нет"}
            >
              {chosen === null && <option value="">— город —</option>}
              {form?.cities.map((c) => (
                <option key={c.id} value={c.id}>
                  «{c.group}»
                </option>
              ))}
            </select>
          )}
        </label>
      )}
    </div>
  );
}
